import { motion } from "framer-motion";
import { AlertCircle, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Section = ({ title, children, testId }) => (
  <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="border border-line bg-white p-6 sm:p-10" data-testid={testId}>
    <h2 className="font-display text-xl font-extrabold tracking-tight text-midnight sm:text-2xl">
      {title}
    </h2>
    <div className="mt-5 space-y-3 text-sm leading-relaxed text-steel sm:text-base">
      {children}
    </div>
  </motion.section>
);

const PolitiqueConfidentialite = () => (
  <>
    <Seo
      title="Politique de confidentialité — ATLAS THD"
      description="Politique de confidentialité d'ATLAS THD : données collectées via le formulaire de contact, finalités, durée de conservation et droits des utilisateurs."
      breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Politique de confidentialité" }]}
    />

    <section className="border-b border-line bg-cloud" data-testid="privacy-hero">
      <div className="bg-blueprint">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
          <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Politique de confidentialité" }]} />
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
          >
            Politique de confidentialité
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 max-w-2xl text-lg text-steel"
          >
            Comment ATLAS THD collecte, utilise et protège les informations
            transmises via ce site.
          </motion.p>
        </div>
      </div>
    </section>

    <div className="bg-cloud py-16 lg:py-20" data-testid="privacy-content">
      <div className="mx-auto max-w-4xl space-y-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-start gap-3 border border-dashed border-amber-400 bg-amber-50 p-4 text-sm text-amber-900" data-testid="privacy-review-notice">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-bold">À valider avant publication :</span>{" "}
            ce document doit être relu par ATLAS THD afin de confirmer les durées
            de conservation et les modalités exactes de traitement.
          </p>
        </div>

        <Section title="Responsable du traitement" testId="privacy-responsable">
          <p>
            Le responsable du traitement des données collectées sur ce site est{" "}
            <span className="font-semibold text-midnight">ATLAS THD</span>,
            joignable par e-mail à{" "}
            <a href="mailto:Contact.pro@atlasthd.com" data-testid="privacy-email" className="font-medium text-midnight underline decoration-terrain/50 underline-offset-4 hover:text-terrain">
              Contact.pro@atlasthd.com
            </a>{" "}
            ou par téléphone au 06 05 76 50 66.
          </p>
        </Section>

        <Section title="Données collectées" testId="privacy-donnees">
          <p>
            Le formulaire de demande de devis collecte les informations
            suivantes : nom et prénom, société (facultatif), adresse e-mail
            professionnelle, numéro de téléphone, type de besoin et description
            du projet.
          </p>
          <p>
            Aucune autre donnée personnelle n'est collectée par le site sans
            action de votre part.
          </p>
        </Section>

        <Section title="Finalités et base légale" testId="privacy-finalites">
          <p>
            Ces données servent exclusivement à traiter votre demande de devis
            ou de contact et à échanger avec vous sur votre projet. Elles ne
            sont ni vendues, ni cédées à des tiers à des fins commerciales.
          </p>
          <p>
            Le traitement repose sur les mesures précontractuelles prises à
            votre demande et sur l'intérêt légitime d'ATLAS THD à répondre aux
            sollicitations reçues.
          </p>
        </Section>

        <Section title="Durée de conservation" testId="privacy-duree">
          <p>
            Les données sont conservées pendant la durée nécessaire au
            traitement de votre demande et au suivi de la relation
            commerciale qui peut en découler.
          </p>
          <p className="text-xs italic">
            Durée précise à confirmer par ATLAS THD avant publication.
          </p>
        </Section>

        <Section title="Mesure d'audience" testId="privacy-audience">
          <p>
            Ce site est hébergé et opéré via la plateforme Emergent, qui
            intègre un outil de mesure d'audience (PostHog) destiné à analyser
            la fréquentation et à améliorer le service. Les données collectées
            à cette fin sont traitées de manière agrégée.
          </p>
        </Section>

        <Section title="Vos droits" testId="privacy-droits">
          <p>
            Conformément au Règlement général sur la protection des données
            (RGPD) et à la loi Informatique et Libertés, vous disposez d'un
            droit d'accès, de rectification, d'effacement, de limitation et
            d'opposition concernant les données vous concernant.
          </p>
          <p>
            Pour exercer ces droits, écrivez à{" "}
            <a href="mailto:Contact.pro@atlasthd.com" data-testid="privacy-rights-email" className="font-medium text-midnight underline decoration-terrain/50 underline-offset-4 hover:text-terrain">
              Contact.pro@atlasthd.com
            </a>
            . Vous pouvez également introduire une réclamation auprès de la
            CNIL (www.cnil.fr).
          </p>
        </Section>

        <Section title="Contact" testId="privacy-contact">
          <p className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-terrain" />
            Pour toute question relative à cette politique : Contact.pro@atlasthd.com
          </p>
        </Section>
      </div>
    </div>
  </>
);

export default PolitiqueConfidentialite;
